/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
    ISignUpEmailVerification, ISignUpItem, ISignUpLoading, ISignUpLocalAccount, ISignUpViewProps
} from '../../../modules/smwe-sign-up/./smwe-sign-up';

const SignUpItem: React.FC<ISignUpItem> = ({
    wrapper,
    label,
    errorMessage,
    input
}) => {

    return (
        <Node {...wrapper}>
            {label}
            {errorMessage}
            {input}
        </Node>
    );
};

const SignUpEmailVerification: React.FC<ISignUpEmailVerification> = ({
    email,
    buttonWrapper,
    buttons,
    successMessage,
    errorMessage
}) => {
    return (
        <>
            <SignUpItem {...email} />
            {successMessage && successMessage.map((message: React.ReactNode) => {
                return (
                    <>
                        {message}
                    </>
                );
            })}
            {errorMessage && errorMessage.map((message: React.ReactNode) => {
                return (
                    <>
                        {message}
                    </>
                );
            })}
            <Node {...buttonWrapper}>
                {buttons && buttons.map((button: React.ReactNode) => {
                    return (
                        <>
                            {button}
                        </>
                    );
                })}
            </Node>
        </>
    );
};

const LocalAccount: React.FC<ISignUpLocalAccount> = ({
    localAccount,
    items,
    emailVerification,
    buttons,
    errorMessage,
    disclaimer,
    pwRequirements
}) => {
    return (
        <Node {...localAccount}>
            {items && items.map((item: ISignUpItem) => {
                const isNewPassword = item.key === 'newPassword';
                if (item.key === 'email' && emailVerification.isRequired) {
                    return (
                        <>
                            <SignUpItem {...item} />
                            <SignUpEmailVerification {...emailVerification} />
                        </>
                    );
                } else if (isNewPassword || item.key === 'reenterPassword') {
                    return (
                        <Node key={item.key} {...item.wrapper}>
                            {isNewPassword && pwRequirements}
                            {item.label}
                            {item.errorMessage}
                            {item.input}
                            <a
                                className='ms-sign-up__password-icon ms-sign-up__password-icon-show'
                                role='button'
                                onClick={_showPassword}
                            />
                        </Node>
                    );
                }
                return (
                    <>
                        <SignUpItem {...item} />
                    </>
                );
            })}
            {errorMessage && errorMessage.map((error: React.ReactNode, index: number) => {
                return (
                    <React.Fragment key={index}>
                        {error}
                    </React.Fragment>
                );
            })}
            {buttons && buttons.map((button: React.ReactNode, index: number) => {
                return (
                    <React.Fragment key={index}>
                        {button}
                    </React.Fragment>
                );
            })}
            {disclaimer}
        </Node>
    );
};

const SignUpLoading: React.FC<ISignUpLoading> = ({
    modal,
    modalBody,
    icon,
    message,
}) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const SignUpView: React.FC<ISignUpViewProps> = props => {
    const {
        signUp,
        viewState,
        loading,
        defaultAADConainer,
        aadConainer,
        heading,
        signUpLocalAccount
    } = props;

    return (
        <Module {...signUp}>
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignUpLoading {...loading} />}
            <Node {...aadConainer}>
                {heading}
                {LocalAccount(signUpLocalAccount)}
            </Node>
        </Module>
    );
};

const _showPassword = (event: React.MouseEvent) => {
    const newPasswordState = document.getElementById('newPassword') as HTMLInputElement;
    const reenterPasswordState = document.getElementById('reenterPassword') as HTMLInputElement;
    const iconElements = document.getElementsByClassName('ms-sign-up__password-icon');
    if (newPasswordState && reenterPasswordState && iconElements) {
        newPasswordState.type = (newPasswordState.type === 'password') ? 'text' : 'password';
        reenterPasswordState.type = (reenterPasswordState.type === 'password') ? 'text' : 'password';
        for (let i = 0; i < iconElements.length; i++) {
            iconElements[i].className = (iconElements[i].className === 'ms-sign-up__password-icon ms-sign-up__password-icon-show')
                ? 'ms-sign-up__password-icon ms-sign-up__password-icon-hide'
                : 'ms-sign-up__password-icon ms-sign-up__password-icon-show';
        }
    }
};

export default SignUpView;
